/* ============================================================================
   Curated Collection JS  —  v2025-08-02-b
   ── Adds variant-image swap **and** updates pinned card links to include
      ?variant=ID so the correct variant loads on click.
============================================================================ */

(() => {
  const GRID_SEL = 'ul#product-grid, ul[id^="product-grid"], .product-grid';

  /* ------------------------------------------------------------------ */
  /* 0. Query-string parsing                                            */
  /* ------------------------------------------------------------------ */
  const qp = new URLSearchParams(location.search);

  let rawFirst = decodeURIComponent((qp.get('first') || '').trim());
  let firstHandle = '';
  let variantFromURL = '';

  if (rawFirst.startsWith('http')) {
    try {
      const u = new URL(rawFirst);
      const segs = u.pathname.split('/').filter(Boolean);
      if (segs[0] === 'products' && segs[1]) firstHandle = segs[1];
      variantFromURL = u.searchParams.get('variant') || '';
    } catch (_) {}
  } else {
    const segs = rawFirst.split('/').filter(Boolean);
    firstHandle = segs.pop();
  }
  if (!firstHandle) return;

  /* variant param (digits only) */
  const variantID = (/^\d+$/.test(qp.get('variant') || variantFromURL))
    ? (qp.get('variant') || variantFromURL)
    : '';

  /* source collection */
  let source = (qp.get('source') || '').trim();
  if (!source) {
    const p = location.pathname.split('/').filter(Boolean);
    if (p[0] === 'collections' && p[1]) source = p[1];
  }
  if (!source) return;

  /* limit */
  const limit = Math.max(1, parseInt(qp.get('limit') || '24', 10));

  const grid = document.querySelector(GRID_SEL);
  if (!grid) return;

  /* ------------------------------------------------------------------ */
  /* 1. Theme-editor preview detection                                  */
  /* ------------------------------------------------------------------ */
  const previewExtras = (() => {
    const list = [];
    ['preview_theme_id', 'preview_theme_token', 'pb'].forEach(k => {
      if (qp.has(k)) list.push(`${k}=${encodeURIComponent(qp.get(k) || '')}`);
    });
    return list.join('&');
  })();
  const appendPreview = url =>
    previewExtras ? (url.includes('?') ? `${url}&${previewExtras}` : `${url}?${previewExtras}`) : url;
  const needsCookie = Boolean(previewExtras);

  /* ------------------------------------------------------------------ */
  /* 2. Helpers                                                         */
  /* ------------------------------------------------------------------ */
  const aspect = (grid.getAttribute('data-aspect') || '').toLowerCase();
  const view =
        aspect === 'square'   ? 'card-square'   :
        aspect === 'portrait' ? 'card-portrait' :
        'card';

  const cacheKey = (h, v) => `card:${view}:${h}:${v||'noVar'}`;
  const getCache = (h, v) => { try { return sessionStorage.getItem(cacheKey(h, v)); } catch { return null; } };
  const setCache = (h, v, html) => { try { sessionStorage.setItem(cacheKey(h, v), html); } catch {} };

  const fetchCard = async (handle, variant='') => {
    const cached = getCache(handle, variant);
    if (cached) return cached;

    const url = appendPreview(
      `/products/${encodeURIComponent(handle)}?view=${view}${variant ? `&variant=${variant}` : ''}`
    );
    const r = await fetch(url, {
      credentials: needsCookie ? 'same-origin' : 'omit',
      cache: 'force-cache'
    });
    if (!r.ok) return '';
    const html = await r.text();
    if (html.trim()) setCache(handle, variant, html);
    return html;
  };

  const render = html => { if (html.trim()) grid.insertAdjacentHTML('beforeend', html); };
  const updateCount = () => {
    const c = grid.querySelectorAll('.grid__item').length;
    document.querySelectorAll('[data-product-count], .product-count, .collection-product-count')
      .forEach(el => el.textContent = `${c} Products`);
  };

  /* ------------------------------------------------------------------ */
  /* 3. Skeleton + parallel fetches                                     */
  /* ------------------------------------------------------------------ */
  grid.innerHTML = '<li class="grid__item">Loading…</li>';

  const pinnedPromise  = fetchCard(firstHandle, variantID);
  const handlesPromise = fetch(
  appendPreview(`/collections/${encodeURIComponent(source)}?view=handles`),
  { credentials: needsCookie ? 'same-origin' : 'omit', cache: 'force-cache' }
)
  .then(async r => {
    if (!r.ok) return { handles: [] };
    const text = await r.text();
    const start = text.indexOf('{');
    const end = text.lastIndexOf('}');
    if (start === -1 || end === -1) return { handles: [] };
    try {
      return JSON.parse(text.slice(start, end + 1));
    } catch {
      return { handles: [] };
    }
  })
  .catch(() => ({ handles: [] }));

  /* ------------------------------------------------------------------ */
  /* 4. Render + variant handling                                       */
  /* ------------------------------------------------------------------ */
  (async () => {
    /* 4-a  pinned */
    const pinnedHTML = await pinnedPromise;
    grid.innerHTML = '';
    render(pinnedHTML);

    /* 4-b  rest */
    const data = await handlesPromise;
    const raw  = Array.isArray(data?.handles) ? data.handles : [];

    const seen = new Set([firstHandle]);
    const restHandles = [];
    for (const h of raw) {
      if (h && !seen.has(h)) { seen.add(h); restHandles.push(h); }
      if (restHandles.length >= Math.max(0, limit - (pinnedHTML ? 1 : 0))) break;
    }

    const cards = await Promise.all(restHandles.map(h => fetchCard(h)));
    cards.forEach(render);
    updateCount();

    /* 4-c  swap pinned image & update links if variant specified */
    if (variantID) {
      try {
        const prod = await fetch(`/products/${firstHandle}.js`).then(r => r.json());
        const varObj = prod.variants.find(v => String(v.id) === variantID);
        if (!varObj || !varObj.featured_image) return;

        const newSrcBase = varObj.featured_image.src;
        const pinnedLi = grid.querySelector('li.grid__item:first-child');
        if (!pinnedLi) return;

        /* swap imgs */
        pinnedLi.querySelectorAll('img').forEach(img => {
          const m = img.src.match(/_(\d+x)\./);
          const sized = m ? newSrcBase.replace(/\.jpg|\.png/i, `_${m[1]}.jpg`) : newSrcBase;
          img.src = sized;
          img.srcset = '';
        });

        /* update all anchors to include ?variant=ID */
        pinnedLi.querySelectorAll('a[href*="/products/"]').forEach(a => {
          const u = new URL(a.href, location.origin);
          u.searchParams.set('variant', variantID);
          a.href = u.pathname + u.search;
        });
      } catch (_) {/* silent */}
    }
  })().catch(() => {
    grid.innerHTML =
      '<li class="grid__item">Failed to load curated list. Check handle/variant/source.</li>';
  });
})();
